import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Toaster } from 'sonner';
import { ConfirmDialogProvider } from '@/components/ui/ConfirmDialog';
import CommandPalette from '@/components/CommandPalette';
import { useAuthStore } from '@/stores/authStore';
import ProtectedRoute from '@/components/ProtectedRoute';
import RoleProtectedRoute from '@/components/RoleProtectedRoute';
import FeatureProtectedRoute from '@/components/FeatureProtectedRoute';
import AppLayout from '@/components/layout/AppLayout';
import PublicLayout from '@/components/layout/PublicLayout';

// Eagerly loaded: homepage (LCP-critical)
import LandingPage from '@/pages/LandingPage';

// Minimal loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Auto-reload on stale chunk after deploy
function lazyWithRetry(importFn: () => Promise<any>) {
  return lazy(() =>
    importFn().catch(() => {
      // Chunk missing after deploy — reload once to get new index.html
      const key = 'chunk_reload';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        // Return a never-resolving promise so React doesn't render an error
        // while the page is reloading
        return new Promise(() => {});
      }
      // Already reloaded once and still failing — clear flag for next time
      sessionStorage.removeItem(key);
      return importFn();
    })
  );
}

// --- Lazy: Public Pages ---
const PricingPage = lazyWithRetry(() => import('@/pages/PricingPage'));
const HowItWorksPage = lazyWithRetry(() => import('@/pages/HowItWorksPage'));

// --- Lazy: Ads Landing Pages ---
const AdsFoodCostPage = lazyWithRetry(() => import('@/pages/ads/AdsFoodCostPage'));

// --- Lazy: SEO Landing Pages ---
const EscandalloPage = lazyWithRetry(() => import('@/pages/seo/EscandalloPage'));
const FoodCostPage = lazyWithRetry(() => import('@/pages/seo/FoodCostPage'));
const CartaDigitalPage = lazyWithRetry(() => import('@/pages/seo/CartaDigitalPage'));
const SoftwareGestionPage = lazyWithRetry(() => import('@/pages/seo/SoftwareGestionPage'));
const ControlHorarioPage = lazyWithRetry(() => import('@/pages/seo/ControlHorarioPage'));
const PlantillaEscandalloPage = lazyWithRetry(() => import('@/pages/seo/PlantillaEscandalloPage'));
const MermasCocinaPage = lazyWithRetry(() => import('@/pages/seo/MermasCocinaPage'));
const IngenieriaMenuPage = lazyWithRetry(() => import('@/pages/seo/IngenieriaMenuPage'));
const OcrFacturasPage = lazyWithRetry(() => import('@/pages/seo/OcrFacturasPage'));
const AlergenosPage = lazyWithRetry(() => import('@/pages/seo/AlergenosPage'));
const MargenBeneficioPage = lazyWithRetry(() => import('@/pages/seo/MargenBeneficioPage'));
const AlternativaHaddockPage = lazyWithRetry(() => import('@/pages/seo/AlternativaHaddockPage'));
const CalculadoraFoodCostPage = lazyWithRetry(() => import('@/pages/seo/CalculadoraFoodCostPage'));
const CalculadoraEscandalloPage = lazyWithRetry(() => import('@/pages/seo/CalculadoraEscandalloPage'));
const PreciosMercadoIndexPage = lazyWithRetry(() => import('@/pages/seo/PreciosMercadoIndexPage'));
const PreciosMercadoProductPage = lazyWithRetry(() => import('@/pages/seo/PreciosMercadoProductPage'));
const IndicePreciosMayoristasPage = lazyWithRetry(() => import('@/pages/seo/IndicePreciosMayoristasPage'));
const IndicePlekoInformePage = lazyWithRetry(() => import('@/pages/seo/IndicePlekoInformePage'));
const CosteoRecetasMexicoPage = lazyWithRetry(() => import('@/pages/seo/CosteoRecetasMexicoPage'));
const SoftwareRestaurantesColombiaPage = lazyWithRetry(() => import('@/pages/seo/SoftwareRestaurantesColombiaPage'));
const SoftwareGastronomicoArgentinaPage = lazyWithRetry(() => import('@/pages/seo/SoftwareGastronomicoArgentinaPage'));
const CosteoRecetasLatamPage = lazyWithRetry(() => import('@/pages/seo/CosteoRecetasLatamPage'));
const CalendarioTemporadaPage = lazyWithRetry(() => import('@/pages/seo/CalendarioTemporadaPage'));
const TemporadaMesPage = lazyWithRetry(() => import('@/pages/seo/TemporadaMesPage'));
const DesperdicioAlimentarioPage = lazyWithRetry(() => import('@/pages/seo/DesperdicioAlimentarioPage'));
const PlantillaFoodCostPage = lazyWithRetry(() => import('@/pages/seo/PlantillaFoodCostPage'));
const SoftwareEscandalloPage = lazyWithRetry(() => import('@/pages/seo/SoftwareEscandalloPage'));
const ComoHacerEscandalloPage = lazyWithRetry(() => import('@/pages/seo/ComoHacerEscandalloPage'));
const EscandalloBebidasPage = lazyWithRetry(() => import('@/pages/seo/EscandalloBebidasPage'));
const EjemploEscandalloPage = lazyWithRetry(() => import('@/pages/seo/EjemploEscandalloPage'));
const EscandalloPanaderiaPage = lazyWithRetry(() => import('@/pages/seo/EscandalloPanaderiaPage'));
const EscandalloPasteleriaPage = lazyWithRetry(() => import('@/pages/seo/EscandalloPasteleriaPage'));
const EscandalloCateringPage = lazyWithRetry(() => import('@/pages/seo/EscandalloCateringPage'));
const CalcularPrecioPlatoPage = lazyWithRetry(() => import('@/pages/seo/CalcularPrecioPlatoPage'));
const EscandalloCocteleriaPage = lazyWithRetry(() => import('@/pages/seo/EscandalloCocteleriaPage'));
const TablaMermasAlimentosPage = lazyWithRetry(() => import('@/pages/seo/TablaMermasAlimentosPage'));
const RentabilidadRestaurantePage = lazyWithRetry(() => import('@/pages/seo/RentabilidadRestaurantePage'));
const ControlCostesRestaurantePage = lazyWithRetry(() => import('@/pages/seo/ControlCostesRestaurantePage'));
const GestionComprasRestaurantePage = lazyWithRetry(() => import('@/pages/seo/GestionComprasRestaurantePage'));
const AppccRestaurantePage = lazyWithRetry(() => import('@/pages/seo/AppccRestaurantePage'));
const InspeccionSanidadRestaurantePage = lazyWithRetry(() => import('@/pages/seo/InspeccionSanidadRestaurantePage'));
const TrazabilidadAlimentariaPage = lazyWithRetry(() => import('@/pages/seo/TrazabilidadAlimentariaPage'));
const CalculadoraPrecioVentaPage = lazyWithRetry(() => import('@/pages/seo/CalculadoraPrecioVentaPage'));
const CalculadoraMargenRestaurantePage = lazyWithRetry(() => import('@/pages/seo/CalculadoraMargenRestaurantePage'));
const PescadoTemporadaPage = lazyWithRetry(() => import('@/pages/seo/PescadoTemporadaPage'));
const CalculadoraMermasPage = lazyWithRetry(() => import('@/pages/seo/CalculadoraMermasPage'));
const MariscoTemporadaPage = lazyWithRetry(() => import('@/pages/seo/MariscoTemporadaPage'));
const SetasTemporadaPage = lazyWithRetry(() => import('@/pages/seo/SetasTemporadaPage'));
const FichaTecnicaCocinaPage = lazyWithRetry(() => import('@/pages/seo/FichaTecnicaCocinaPage'));
const InventarioRestaurantePage = lazyWithRetry(() => import('@/pages/seo/InventarioRestaurantePage'));
const CosteMateriaPrimaPage = lazyWithRetry(() => import('@/pages/seo/CosteMateriaPrimaPage'));
const RatioFoodCostIdealPage = lazyWithRetry(() => import('@/pages/seo/RatioFoodCostIdealPage'));
const CalcularCostePlatoPage = lazyWithRetry(() => import('@/pages/seo/CalcularCostePlatoPage'));
const CalculadoraRoiPage = lazyWithRetry(() => import('@/pages/seo/CalculadoraRoiPage'));
const EscanearAlbaranesPage = lazyWithRetry(() => import('@/pages/seo/EscanearAlbaranesPage'));
const OcrAlbaranesPage = lazyWithRetry(() => import('@/pages/seo/OcrAlbaranesPage'));
const DigitalizarFacturasPage = lazyWithRetry(() => import('@/pages/seo/DigitalizarFacturasPage'));
const EmbedCalculadoraShowcasePage = lazyWithRetry(() => import('@/pages/seo/EmbedCalculadoraShowcasePage'));
const IntegracionSquarePage = lazyWithRetry(() => import('@/pages/seo/IntegracionSquarePage'));
const IntegracionLastappPage = lazyWithRetry(() => import('@/pages/seo/IntegracionLastappPage'));
const IntegracionEposNowPage = lazyWithRetry(() => import('@/pages/seo/IntegracionEposNowPage'));

// --- Lazy: Embed Pages (standalone, no layout) ---
const EmbedCalculadoraPage = lazyWithRetry(() => import('@/pages/EmbedCalculadoraPage'));

// --- Lazy: Blog Pages ---
const BlogPage = lazyWithRetry(() => import('@/pages/seo/BlogPage'));
const ComoReducirFoodCostPage = lazyWithRetry(() => import('@/pages/blog/ComoReducirFoodCostPage'));
const ErroresControlCostesPage = lazyWithRetry(() => import('@/pages/blog/ErroresControlCostesPage'));
const InventarioRestauranteGuiaPage = lazyWithRetry(() => import('@/pages/blog/InventarioRestauranteGuiaPage'));
const FoodCostIdealTipoRestaurantePage = lazyWithRetry(() => import('@/pages/blog/FoodCostIdealTipoRestaurantePage'));
const RecetaEstandarRestaurantePage = lazyWithRetry(() => import('@/pages/blog/RecetaEstandarRestaurantePage'));

// --- Lazy: 404 Page ---
const NotFoundPage = lazyWithRetry(() => import('@/pages/NotFoundPage'));

// --- Lazy: Auth Pages ---
const LoginPage = lazyWithRetry(() => import('@/pages/LoginPage'));
const RegisterPage = lazyWithRetry(() => import('@/pages/RegisterPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('@/pages/ResetPasswordPage'));
const EmailVerifyPage = lazyWithRetry(() => import('@/pages/EmailVerifyPage'));
const PrivacyPolicyPage = lazyWithRetry(() => import('@/pages/PrivacyPolicyPage'));
const TermsPage = lazyWithRetry(() => import('@/pages/TermsPage'));
const CookiePolicyPage = lazyWithRetry(() => import('@/pages/CookiePolicyPage'));
const RefundPolicyPage = lazyWithRetry(() => import('@/pages/RefundPolicyPage'));
const OnboardingPage = lazyWithRetry(() => import('@/pages/OnboardingPage'));
const AcceptInvitationPage = lazyWithRetry(() => import('@/pages/AcceptInvitationPage'));

// --- Lazy: Protected Pages ---
const DashboardPage = lazyWithRetry(() => import('@/pages/DashboardPage'));
const IngredientsPage = lazyWithRetry(() => import('@/pages/IngredientsPage'));
const IngredientDetailPage = lazyWithRetry(() => import('@/pages/IngredientDetailPage'));
const IngredientFormPage = lazyWithRetry(() => import('@/pages/IngredientFormPage'));
const CategoriesPage = lazyWithRetry(() => import('@/pages/CategoriesPage'));
const MarketPriceComparisonPage = lazyWithRetry(() => import('@/pages/MarketPriceComparisonPage'));
const RecipeOptimizerPage = lazyWithRetry(() => import('@/pages/RecipeOptimizerPage'));
const RecipeSimulatorPage = lazyWithRetry(() => import('@/pages/RecipeSimulatorPage'));
const RecipesPage = lazyWithRetry(() => import('@/pages/RecipesPage'));
const RecipeDetailPage = lazyWithRetry(() => import('@/pages/RecipeDetailPage'));
const RecipeFormPage = lazyWithRetry(() => import('@/pages/RecipeFormPage'));
const DespiecesPage = lazyWithRetry(() => import('@/pages/DespiecesPage'));
const DespieceFormPage = lazyWithRetry(() => import('@/pages/DespieceFormPage'));
const DespieceDetailPage = lazyWithRetry(() => import('@/pages/DespieceDetailPage'));
const MenusPage = lazyWithRetry(() => import('@/pages/MenusPage'));
const CartasPage = lazyWithRetry(() => import('@/pages/CartasPage'));
const CartaDesignerPage = lazyWithRetry(() => import('@/pages/CartaDesignerPage'));
const PublicCartaPage = lazyWithRetry(() => import('@/pages/PublicCartaPage'));
const OrdersPage = lazyWithRetry(() => import('@/pages/OrdersPage'));
const OrderDetailPage = lazyWithRetry(() => import('@/pages/OrderDetailPage'));
const SuppliersPage = lazyWithRetry(() => import('@/pages/SuppliersPage'));
const SupplierDetailPage = lazyWithRetry(() => import('@/pages/SupplierDetailPage'));
const SupplierFormPage = lazyWithRetry(() => import('@/pages/SupplierFormPage'));
const TrialExpiredPage = lazyWithRetry(() => import('@/pages/TrialExpiredPage'));
const UpgradePage = lazyWithRetry(() => import('@/pages/UpgradePage'));
const SettingsPage = lazyWithRetry(() => import('@/pages/SettingsPage'));
const ReportsPage = lazyWithRetry(() => import('@/pages/ReportsPage'));
const AlbaranesPage = lazyWithRetry(() => import('@/pages/AlbaranesPage'));
const ReconciliationPage = lazyWithRetry(() => import('@/pages/ReconciliationPage'));
const PurchasePlannerPage = lazyWithRetry(() => import('@/pages/PurchasePlannerPage'));
const TimeClockPage = lazyWithRetry(() => import('@/pages/TimeClockPage'));
const SalesPage = lazyWithRetry(() => import('@/pages/SalesPage'));
const SimulatorPage = lazyWithRetry(() => import('@/pages/SimulatorPage'));
const OptimizerPage = lazyWithRetry(() => import('@/pages/OptimizerPage'));
const LabPage = lazyWithRetry(() => import('@/pages/LabPage'));
const HelpPage = lazyWithRetry(() => import('@/pages/HelpPage'));
const StockPage = lazyWithRetry(() => import('@/pages/StockPage'));
const PnlPage = lazyWithRetry(() => import('@/pages/PnlPage'));
const TraceabilityPage = lazyWithRetry(() => import('@/pages/TraceabilityPage'));
const FoodLabelPage = lazyWithRetry(() => import('@/pages/FoodLabelPage'));
const ProductionHistoryPage = lazyWithRetry(() => import('@/pages/ProductionHistoryPage'));
const ImportHubPage = lazyWithRetry(() => import('@/pages/ImportHubPage'));
const SmartPurchaseListPage = lazyWithRetry(() => import('@/pages/SmartPurchaseListPage'));

// --- Lazy: Admin Pages ---
const AdminLayout = lazyWithRetry(() => import('@/components/admin/AdminLayout'));
const AdminDashboardPage = lazyWithRetry(() => import('@/pages/admin/AdminDashboardPage'));
const AdminUsersPage = lazyWithRetry(() => import('@/pages/admin/AdminUsersPage'));
const AdminUserDetailPage = lazyWithRetry(() => import('@/pages/admin/AdminUserDetailPage'));
const AdminPricingPage = lazyWithRetry(() => import('@/pages/admin/AdminPricingPage'));
const AdminPartnersPage = lazyWithRetry(() => import('@/pages/admin/AdminPartnersPage'));
const AdminWebWhatsAppPage = lazyWithRetry(() => import('@/pages/admin/AdminWebWhatsAppPage'));
const AdminAnalyticsPage = lazyWithRetry(() => import('@/pages/admin/AdminAnalyticsPage'));
const AdminAuditLogsPage = lazyWithRetry(() => import('@/pages/admin/AdminAuditLogsPage'));
const AdminMarketScrapesPage = lazyWithRetry(() => import('@/pages/admin/AdminMarketScrapesPage'));
const CrmPage = lazyWithRetry(() => import('@/pages/admin/CrmPage'));
const AdminMarketScrapeDetailPage = lazyWithRetry(() => import('@/pages/admin/AdminMarketScrapeDetailPage'));
const AdminOcrScansPage = lazyWithRetry(() => import('@/pages/admin/AdminOcrScansPage'));
const TimeTrackingManagementPage = lazyWithRetry(() => import('@/pages/TimeTrackingManagementPage'));

function App() {
  const { checkAuth, isAuthenticated, isLoading } = useAuthStore();

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  return (
    <ConfirmDialogProvider>
    <BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          style: { fontSize: '14px' },
          duration: 3000,
        }}
        richColors
        closeButton
      />
      <ScrollToTop />
      <CommandPalette />
      <Analytics />
      <SpeedInsights />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public marketing routes with PublicLayout */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/precios" element={<PricingPage />} />
          <Route path="/como-funciona" element={<HowItWorksPage />} />
          <Route path="/privacidad" element={<PrivacyPolicyPage />} />
          <Route path="/terminos" element={<TermsPage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />
          <Route path="/reembolsos" element={<RefundPolicyPage />} />
          {/* SEO landing pages */}
          <Route path="/escandallo" element={<EscandalloPage />} />
          <Route path="/food-cost" element={<FoodCostPage />} />
          <Route path="/carta-digital-restaurante" element={<CartaDigitalPage />} />
          <Route path="/software-gestion-restaurante" element={<SoftwareGestionPage />} />
          <Route path="/control-horario-hosteleria" element={<ControlHorarioPage />} />
          <Route path="/plantilla-escandallo-excel" element={<PlantillaEscandalloPage />} />
          <Route path="/mermas-cocina" element={<MermasCocinaPage />} />
          <Route path="/ingenieria-de-menu" element={<IngenieriaMenuPage />} />
          <Route path="/ocr-facturas-restaurante" element={<OcrFacturasPage />} />
          <Route path="/escanear-albaranes-restaurante" element={<EscanearAlbaranesPage />} />
          <Route path="/ocr-albaranes-hosteleria" element={<OcrAlbaranesPage />} />
          <Route path="/digitalizar-facturas-restaurante" element={<DigitalizarFacturasPage />} />
          <Route path="/embed-calculadora-food-cost" element={<EmbedCalculadoraShowcasePage />} />
          <Route path="/alergenos-restaurante" element={<AlergenosPage />} />
          <Route path="/margen-beneficio-restaurante" element={<MargenBeneficioPage />} />
          <Route path="/alternativa-haddock" element={<AlternativaHaddockPage />} />
          <Route path="/calculadora-food-cost" element={<CalculadoraFoodCostPage />} />
          <Route path="/calculadora-escandallo" element={<CalculadoraEscandalloPage />} />
          {/* LATAM SEO landing pages */}
          <Route path="/costeo-recetas-mexico" element={<CosteoRecetasMexicoPage />} />
          <Route path="/software-restaurantes-colombia" element={<SoftwareRestaurantesColombiaPage />} />
          <Route path="/software-gastronomico-argentina" element={<SoftwareGastronomicoArgentinaPage />} />
          <Route path="/costeo-recetas-latinoamerica" element={<CosteoRecetasLatamPage />} />
          <Route path="/calendario-temporada" element={<CalendarioTemporadaPage />} />
          <Route path="/productos-temporada/:mes" element={<TemporadaMesPage />} />
          <Route path="/desperdicio-alimentario-restaurante" element={<DesperdicioAlimentarioPage />} />
          <Route path="/plantilla-food-cost" element={<PlantillaFoodCostPage />} />
          <Route path="/software-escandallo" element={<SoftwareEscandalloPage />} />
          <Route path="/como-hacer-escandallo" element={<ComoHacerEscandalloPage />} />
          <Route path="/escandallo-bebidas" element={<EscandalloBebidasPage />} />
          <Route path="/ejemplo-escandallo" element={<EjemploEscandalloPage />} />
          <Route path="/escandallo-panaderia" element={<EscandalloPanaderiaPage />} />
          <Route path="/escandallo-pasteleria" element={<EscandalloPasteleriaPage />} />
          <Route path="/calcular-precio-plato" element={<CalcularPrecioPlatoPage />} />
          <Route path="/escandallo-catering" element={<EscandalloCateringPage />} />
          <Route path="/escandallo-cocteleria" element={<EscandalloCocteleriaPage />} />
          <Route path="/tabla-mermas-alimentos" element={<TablaMermasAlimentosPage />} />
          <Route path="/rentabilidad-restaurante" element={<RentabilidadRestaurantePage />} />
          <Route path="/control-costes-restaurante" element={<ControlCostesRestaurantePage />} />
          <Route path="/gestion-compras-restaurante" element={<GestionComprasRestaurantePage />} />
          <Route path="/appcc-restaurante" element={<AppccRestaurantePage />} />
          <Route path="/inspeccion-sanidad-restaurante" element={<InspeccionSanidadRestaurantePage />} />
          <Route path="/trazabilidad-alimentaria" element={<TrazabilidadAlimentariaPage />} />
          <Route path="/calculadora-precio-venta" element={<CalculadoraPrecioVentaPage />} />
          <Route path="/calculadora-margen-restaurante" element={<CalculadoraMargenRestaurantePage />} />
          <Route path="/pescado-de-temporada" element={<PescadoTemporadaPage />} />
          <Route path="/calculadora-mermas" element={<CalculadoraMermasPage />} />
          <Route path="/marisco-de-temporada" element={<MariscoTemporadaPage />} />
          <Route path="/setas-de-temporada" element={<SetasTemporadaPage />} />
          <Route path="/ficha-tecnica-cocina" element={<FichaTecnicaCocinaPage />} />
          <Route path="/inventario-restaurante" element={<InventarioRestaurantePage />} />
          <Route path="/coste-materia-prima-restaurante" element={<CosteMateriaPrimaPage />} />
          <Route path="/ratio-food-cost-ideal" element={<RatioFoodCostIdealPage />} />
          <Route path="/calcular-coste-plato" element={<CalcularCostePlatoPage />} />
          <Route path="/calculadora-ahorro-restaurante" element={<CalculadoraRoiPage />} />
          <Route path="/precios-mercado" element={<PreciosMercadoIndexPage />} />
          <Route path="/precios-mercado/:slug" element={<PreciosMercadoProductPage />} />
          <Route path="/indice-precios-mayoristas" element={<IndicePreciosMayoristasPage />} />

          {/* POS integration landings. These three pages, their canonical URLs and their
              sitemap entries all already existed; only the routes were missing, so the
              prerender rendered the NotFound page into them and Google was handed three
              404s that looked like real pages. */}
          <Route path="/integracion-square" element={<IntegracionSquarePage />} />
          <Route path="/integracion-lastapp" element={<IntegracionLastappPage />} />
          <Route path="/integracion-epos-now" element={<IntegracionEposNowPage />} />
          {/* Blog */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/como-reducir-food-cost-restaurante" element={<ComoReducirFoodCostPage />} />
          <Route path="/blog/errores-control-costes-restaurante" element={<ErroresControlCostesPage />} />
          <Route path="/blog/inventario-restaurante-guia-completa" element={<InventarioRestauranteGuiaPage />} />
          <Route path="/blog/food-cost-ideal-tipo-restaurante" element={<FoodCostIdealTipoRestaurantePage />} />
          <Route path="/blog/receta-estandar-restaurante" element={<RecetaEstandarRestaurantePage />} />
        </Route>

        {/* Embed widget (no layout - standalone for iframe) */}
        <Route path="/embed/calculadora-food-cost" element={<EmbedCalculadoraPage />} />

        {/* Ads landing pages (no layout - minimal distraction) */}
        <Route path="/ads/food-cost" element={<AdsFoodCostPage />} />

        {/* Printable report (no layout - standalone A4 document) */}
        <Route path="/indice-precios-mayoristas/informe" element={<IndicePlekoInformePage />} />

        {/* Auth routes (no layout) */}
        <Route
          path="/login"
          element={
            !isLoading && isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <LoginPage />
            )
          }
        />
        <Route
          path="/register"
          element={
            !isLoading && isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <RegisterPage />
            )
          }
        />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/email/verify/:id/:hash" element={<EmailVerifyPage />} />
        <Route path="/invitations/:token" element={<AcceptInvitationPage />} />

        {/* Public carta view (no auth required) */}
        <Route path="/c/:hash" element={<PublicCartaPage />} />

        {/* Onboarding (protected, no sidebar) */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* Trial expired page (protected, no sidebar) */}
        <Route
          path="/trial-expired"
          element={
            <ProtectedRoute>
              <TrialExpiredPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes with AppLayout */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/ingredients" element={<IngredientsPage />} />
          <Route path="/ingredients/categories" element={<CategoriesPage />} />
          <Route path="/ingredients/new" element={<IngredientFormPage />} />
          <Route path="/ingredients/market-comparison" element={<MarketPriceComparisonPage />} />
          <Route path="/ingredients/:id" element={<IngredientDetailPage />} />
          <Route path="/ingredients/:id/edit" element={<IngredientFormPage />} />
          <Route path="/recipes" element={<RecipesPage />} />
          <Route path="/recipes/new" element={<RecipeFormPage />} />
          <Route path="/recipes/:id" element={<RecipeDetailPage />} />
          <Route path="/recipes/:id/edit" element={<RecipeFormPage />} />
          <Route path="/recipes/:id/optimize" element={<RecipeOptimizerPage />} />
          <Route path="/despieces" element={<DespiecesPage />} />
          <Route path="/despieces/new" element={<DespieceFormPage />} />
          <Route path="/despieces/:id" element={<DespieceDetailPage />} />
          <Route path="/despieces/:id/edit" element={<DespieceFormPage />} />
          <Route path="/recipes/:id/simulate" element={<RecipeSimulatorPage />} />
          <Route path="/lab" element={<LabPage />} />
          <Route path="/simulator" element={<SimulatorPage />} />
          <Route path="/simulator/:id" element={<RecipeSimulatorPage />} />
          <Route path="/optimizer" element={<OptimizerPage />} />
          <Route path="/optimizer/:id" element={<RecipeOptimizerPage />} />
          <Route path="/importar" element={<ImportHubPage />} />
          <Route path="/menus" element={<MenusPage />} />
          <Route path="/cartas" element={<CartasPage />} />
          <Route path="/cartas/:id/designer" element={<CartaDesignerPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:id" element={<OrderDetailPage />} />
          <Route path="/compras" element={<SmartPurchaseListPage />} />
          <Route path="/purchase-planner" element={<PurchasePlannerPage />} />
          <Route path="/upgrade" element={<UpgradePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/ayuda" element={<HelpPage />} />
          <Route path="/almacen" element={<StockPage />} />
          <Route path="/etiquetas" element={<FoodLabelPage />} />
          <Route path="/produccion" element={<ProductionHistoryPage />} />
          <Route path="/trazabilidad" element={<TraceabilityPage />} />
          <Route path="/time-clock" element={<TimeClockPage />} />
          <Route path="/sales" element={<SalesPage />} />
          <Route
            path="/time-tracking"
            element={
              <RoleProtectedRoute allowedRoles={['owner', 'admin']}>
                <TimeTrackingManagementPage />
              </RoleProtectedRoute>
            }
          />
          <Route path="/suppliers" element={<SuppliersPage />} />
          <Route path="/suppliers/new" element={<SupplierFormPage />} />
          <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
          <Route path="/suppliers/:id/edit" element={<SupplierFormPage />} />
          <Route path="/albaranes" element={<AlbaranesPage />} />
          <Route path="/conciliacion" element={<ReconciliationPage />} />
          <Route
            path="/pnl"
            element={
              <RoleProtectedRoute allowedRoles={['owner', 'admin']}>
                <FeatureProtectedRoute
                  feature="accounting"
                  title="La Cuenta de Resultados es una función PRO"
                  description="Lleva tu P&L con datos reales: ingresos desglosados, coste de personal, prime cost y EBITDA, mes a mes. Disponible en los planes PRO y SCALE."
                >
                  <PnlPage />
                </FeatureProtectedRoute>
              </RoleProtectedRoute>
            }
          />
          <Route path="/reports" element={<ReportsPage />} />
        </Route>

        {/* Admin routes with AdminLayout */}
        <Route
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/users/:id" element={<AdminUserDetailPage />} />
          <Route path="/admin/pricing" element={<AdminPricingPage />} />
          <Route path="/admin/partners" element={<AdminPartnersPage />} />
          <Route path="/admin/web-whatsapp" element={<AdminWebWhatsAppPage />} />
          <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
          <Route path="/admin/audit-logs" element={<AdminAuditLogsPage />} />
          <Route path="/admin/market-scrapes" element={<AdminMarketScrapesPage />} />
          <Route path="/admin/market-scrapes/:id" element={<AdminMarketScrapeDetailPage />} />
          <Route path="/admin/ocr-scans" element={<AdminOcrScansPage />} />
          <Route path="/admin/crm" element={<CrmPage />} />
        </Route>

        {/* 404 — custom page with SEO links */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
    </ConfirmDialogProvider>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default App;
